import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconUserPlus,
  IconUser,
  IconAt,
  IconLock,
  IconEye,
  IconEyeOff,
  IconX,
} from '@tabler/icons-react';
import { useNotifications } from '../../hooks/useNotifications';
import { usePostRoleManagementUsers } from '../../api/role-management-api/role-management-api';
import { TenantAppRoleDto } from '../../api/model';
import ConfirmationDialog from '../ui/ConfirmationDialog/ConfirmationDialog';
import styles from './ManageUsers.module.scss';

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  tenantRoles: TenantAppRoleDto[];
  isLoadingRoles: boolean;
}

interface FormState {
  username: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  tenantRoleId: string;
}

const emptyForm: FormState = {
  username: '',
  password: '',
  confirmPassword: '',
  firstName: '',
  lastName: '',
  tenantRoleId: '',
};

const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  tenantRoles,
  isLoadingRoles,
}) => {
  const { t } = useTranslation('settings');
  const { showSuccess, showError } = useNotifications();
  const createMutation = usePostRoleManagementUsers();

  const [formData, setFormData] = useState<FormState>(emptyForm);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errors, setErrors] = useState<{
    username?: string;
    password?: string;
    confirmPassword?: string;
    firstName?: string;
    lastName?: string;
  }>({});

  if (!isOpen) return null;

  const reset = () => {
    setFormData(emptyForm);
    setErrors({});
    setShowPassword(false);
    setShowConfirm(false);
  };

  const handleClose = () => {
    if (createMutation.isPending) return;
    reset();
    onClose();
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t('user.manageUsers.validation.firstNameRequired');
    } else if (formData.firstName.length < 2) {
      newErrors.firstName = t('user.manageUsers.validation.firstNameMin');
    } else if (formData.firstName.length > 50) {
      newErrors.firstName = t('user.manageUsers.validation.firstNameMax');
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = t('user.manageUsers.validation.lastNameRequired');
    } else if (formData.lastName.length < 2) {
      newErrors.lastName = t('user.manageUsers.validation.lastNameMin');
    } else if (formData.lastName.length > 50) {
      newErrors.lastName = t('user.manageUsers.validation.lastNameMax');
    }

    if (!formData.username.trim()) {
      newErrors.username = t('user.manageUsers.validation.usernameRequired');
    } else if (formData.username.length < 3) {
      newErrors.username = t('user.manageUsers.validation.usernameMin');
    } else if (formData.username.length > 30) {
      newErrors.username = t('user.manageUsers.validation.usernameMax');
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = t('user.manageUsers.validation.usernamePattern');
    }

    if (!formData.password) {
      newErrors.password = t('user.manageUsers.validation.passwordRequired');
    } else if (formData.password.length < 8) {
      newErrors.password = t('user.manageUsers.validation.passwordMin');
    } else if (formData.password.length > 128) {
      newErrors.password = t('user.manageUsers.validation.passwordMax');
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('user.manageUsers.validation.confirmRequired');
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('user.manageUsers.validation.passwordsMismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setConfirmOpen(true);
  };

  const doCreate = () => {
    createMutation.mutate(
      {
        data: {
          username: formData.username.trim(),
          password: formData.password,
          role: 'USER' as any,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          active: true,
          tenantRoleId: formData.tenantRoleId ? Number(formData.tenantRoleId) : undefined,
        },
      },
      {
        onSuccess: () => {
          showSuccess(t('user.manageUsers.feedback.created'), t('user.manageUsers.feedback.createdMsg'));
          reset();
          onCreated();
          onClose();
        },
        onError: () => {
          showError(t('user.manageUsers.feedback.failed'), t('user.manageUsers.feedback.failedMsg'));
        },
      }
    );
  };

  const fullName = `${formData.firstName} ${formData.lastName}`.trim() || formData.username;

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderText}>
            <IconUserPlus size={20} className={styles.modalHeaderIcon} />
            <div>
              <h3 className={styles.modalTitle}>{t('user.manageUsers.addTitle')}</h3>
              <p className={styles.modalSubtitle}>{t('user.manageUsers.addSubtitle')}</p>
            </div>
          </div>
          <button type="button" className={styles.modalClose} onClick={handleClose} aria-label={t('user.manageUsers.cancel', 'Cancel')}>
            <IconX size={18} />
          </button>
        </div>

        <form className={styles.modalForm} onSubmit={handleSubmit}>
          <div className={styles.formSection}>
            <span className={styles.sectionLabel}>{t('user.manageUsers.sectionPersonal')}</span>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="firstName">{t('user.manageUsers.fields.firstName')} <span className={styles.required}>*</span></label>
                <div className={`${styles.inputWrapper} ${errors.firstName ? styles.inputWrapperError : ''}`}>
                  <IconUser size={18} className={styles.inputIcon} />
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={e => { handleChange(e); if (errors.firstName) setErrors(prev => ({ ...prev, firstName: undefined })); }}
                    placeholder={t('user.manageUsers.fields.firstNamePlaceholder')}
                  />
                </div>
                {errors.firstName && <span className={styles.fieldError}>{errors.firstName}</span>}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="lastName">{t('user.manageUsers.fields.lastName')} <span className={styles.required}>*</span></label>
                <div className={`${styles.inputWrapper} ${errors.lastName ? styles.inputWrapperError : ''}`}>
                  <IconUser size={18} className={styles.inputIcon} />
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={e => { handleChange(e); if (errors.lastName) setErrors(prev => ({ ...prev, lastName: undefined })); }}
                    placeholder={t('user.manageUsers.fields.lastNamePlaceholder')}
                  />
                </div>
                {errors.lastName && <span className={styles.fieldError}>{errors.lastName}</span>}
              </div>
            </div>
          </div>

          <div className={styles.formSection}>
            <span className={styles.sectionLabel}>{t('user.manageUsers.sectionCredentials')}</span>
            <div className={styles.formGroup}>
              <label htmlFor="username">{t('user.manageUsers.fields.username')} <span className={styles.required}>*</span></label>
              <div className={`${styles.inputWrapper} ${errors.username ? styles.inputWrapperError : ''}`}>
                <IconAt size={18} className={styles.inputIcon} />
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={e => { handleChange(e); if (errors.username) setErrors(prev => ({ ...prev, username: undefined })); }}
                  placeholder={t('user.manageUsers.fields.usernamePlaceholder')}
                />
              </div>
              {errors.username && <span className={styles.fieldError}>{errors.username}</span>}
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="password">{t('user.manageUsers.fields.password')} <span className={styles.required}>*</span></label>
                <div className={`${styles.inputWrapper} ${errors.password ? styles.inputWrapperError : ''}`}>
                  <IconLock size={18} className={styles.inputIcon} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={e => { handleChange(e); if (errors.password) setErrors(prev => ({ ...prev, password: undefined })); }}
                    placeholder={t('user.manageUsers.fields.passwordPlaceholder')}
                  />
                  <button type="button" className={styles.passwordToggle} onClick={() => setShowPassword(v => !v)} tabIndex={-1}>
                    {showPassword ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                  </button>
                </div>
                {errors.password && <span className={styles.fieldError}>{errors.password}</span>}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="confirmPassword">{t('user.manageUsers.fields.confirmPassword')} <span className={styles.required}>*</span></label>
                <div className={`${styles.inputWrapper} ${errors.confirmPassword ? styles.inputWrapperError : ''}`}>
                  <IconLock size={18} className={styles.inputIcon} />
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={e => { handleChange(e); if (errors.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: undefined })); }}
                    placeholder={t('user.manageUsers.fields.confirmPasswordPlaceholder')}
                  />
                  <button type="button" className={styles.passwordToggle} onClick={() => setShowConfirm(v => !v)} tabIndex={-1}>
                    {showConfirm ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                  </button>
                </div>
                {errors.confirmPassword && <span className={styles.fieldError}>{errors.confirmPassword}</span>}
              </div>
            </div>
          </div>

          <div className={styles.formSection}>
            <span className={styles.sectionLabel}>{t('user.manageUsers.sectionRole')}</span>
            <div className={styles.formGroup}>
              <label htmlFor="tenantRoleId">{t('user.manageUsers.fields.tenantRole')}</label>
              <select
                id="tenantRoleId"
                name="tenantRoleId"
                className={styles.select}
                value={formData.tenantRoleId}
                onChange={handleChange}
                disabled={isLoadingRoles}
              >
                <option value="">
                  {isLoadingRoles
                    ? t('user.manageUsers.assignRole.loadingRoles')
                    : t('user.manageUsers.fields.tenantRoleNone')}
                </option>
                {tenantRoles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.displayName}
                  </option>
                ))}
              </select>
              <span className={styles.fieldHint}>{t('user.manageUsers.fields.tenantRoleHint')}</span>
            </div>
          </div>

          <div className={styles.modalActions}>
            <button type="button" className={styles.resetButton} onClick={handleClose} disabled={createMutation.isPending}>
              {t('user.manageUsers.cancel', 'Cancel')}
            </button>
            <button type="submit" className={styles.submitButton} disabled={createMutation.isPending}>
              {createMutation.isPending ? t('user.manageUsers.creating') : t('user.manageUsers.create')}
            </button>
          </div>
        </form>
      </div>

      <ConfirmationDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={doCreate}
        type="confirm"
        title={t('user.manageUsers.confirm.createTitle', 'Create user?')}
        message={t('user.manageUsers.confirm.createMessage', {
          name: fullName,
          username: formData.username,
          defaultValue: 'Create a new account for {{name}} (@{{username}})?',
        })}
        confirmText={t('user.manageUsers.create')}
        cancelText={t('user.manageUsers.cancel', 'Cancel')}
      />
    </div>
  );
};

export default AddUserModal;
